import { usePairById, usePairDayDatas } from 'hooks/subgraph/v2'
import { V2PairByIdQuery, V2PairDayDatasQuery } from 'queries/v2/generated'
import { useMemo } from 'react'

type PairFromQuery = NonNullable<V2PairByIdQuery['pair']>
type PairDayDataFromQuery = V2PairDayDatasQuery['pairDayDatas'][number]

// queryPairById()
export type SubgraphV2Pool = {
  id: string
  name: string
  symbol: string
  token0: {
    id: string
    symbol: string
    decimals: number
  }
  token1: {
    id: string
    symbol: string
    decimals: number
  }
  token0Amount: string
  weeklyFeesEarnedUSD: string
  token1Amount: string
  token0AmountUSD: string
  token1AmountUSD: string
  totalValueLockedUSD: string
  volumeUSD: string
  apr: {
    averageApr: string
  }
  pool: {
    id: string
    feeTier: number
  }
} & PairFromQuery

export interface UsePrepareSubgraphV2PoolResult {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  pool: SubgraphV2Pool | null
  error: any
  refetch: () => void
}

function prepareV2Pool(pair: PairFromQuery, pairDayDatas?: PairDayDataFromQuery[]): SubgraphV2Pool {
  // Calculate USD values using derivedETH
  const token0AmountUSD = (parseFloat(pair.reserve0) * parseFloat(pair.token0.derivedETH || '0')).toString()

  const token1AmountUSD = (parseFloat(pair.reserve1) * parseFloat(pair.token1.derivedETH || '0')).toString()

  // Calculate APR (0.3% fee * daily volume * 365 / TVL)
  const aprCalc =
    parseFloat(pair.reserveUSD) > 0
      ? ((parseFloat(pair.volumeUSD) * 0.003 * 365) / parseFloat(pair.reserveUSD)) * 100
      : 0

  const weeklyFeesEarnedUSD = calculateWeeklyFees(pair, pairDayDatas)

  return {
    ...pair,
    name: `${pair.token0.symbol}-${pair.token1.symbol}`,
    symbol: `${pair.token0.symbol}-${pair.token1.symbol}`,
    token0Amount: pair.reserve0,
    token1Amount: pair.reserve1,
    token0AmountUSD,
    token1AmountUSD,
    weeklyFeesEarnedUSD,
    totalValueLockedUSD: pair.reserveUSD,
    volumeUSD: pair.volumeUSD,
    apr: {
      averageApr: aprCalc.toString(),
    },
    pool: {
      id: pair.id,
      feeTier: 3000,
    },
  }
}

/**
 * Calculate weekly fees based on available day data
 * @param pair Base pair data
 * @param pairDayDatas Day data from the API
 * @returns Weekly fees as a string
 */
function calculateWeeklyFees(pair: PairFromQuery, pairDayDatas?: PairDayDataFromQuery[]): string {
  if (pairDayDatas && pairDayDatas.length >= 7) {
    const last7Days = pairDayDatas.slice(0, 7)
    const weeklyVolume = last7Days.reduce((sum, day) => sum + parseFloat(day.dailyVolumeUSD), 0)
    return (weeklyVolume * 0.003).toString()
  } else {
    return ((parseFloat(pair.volumeUSD) / 30) * 7 * 0.003).toString()
  }
}

/**
 * Hook to get V2 pool data with APR calculated the same way as the chart
 */
export function useV2Pool(poolAddress: string | undefined): UsePrepareSubgraphV2PoolResult {
  const shouldSkip = !poolAddress

  const { data, isLoading, isFetching, isError, refetch, error, isPending } = usePairById(poolAddress ?? '', {
    skip: shouldSkip,
  })
  const isUninitialized = shouldSkip || isPending

  const { data: dayData } = usePairDayDatas(
    {
      pairAddress: poolAddress ?? '',
      skip: 0,
    },
    {
      skip: shouldSkip,
    }
  )

  const pairDayDatas = useMemo(() => dayData?.pairDayDatas ?? [], [dayData])

  const preparedData: SubgraphV2Pool | null = useMemo(() => {
    if (data?.pair) {
      return prepareV2Pool(data.pair, pairDayDatas)
    }
    return null
  }, [data, pairDayDatas])

  return {
    isLoading,
    isFetching,
    isUninitialized,
    isError,
    error,
    pool: preparedData,
    refetch,
  }
}

export function useV2PoolDayData(poolAddress: string | undefined) {
  const shouldSkip = !poolAddress
  const { data } = usePairDayDatas(
    {
      pairAddress: poolAddress ?? '',
      skip: 0,
    },
    {
      skip: shouldSkip,
    }
  )

  return useMemo(() => data?.pairDayDatas ?? [], [data])
}
